import React from 'react'
import { Trans } from '@lingui/react'
import cn from 'classnames'

import './user-settings-privacy.styl'

import { Heading } from 'client/shared/blocks/heading/heading'
import Spacer from 'client/shared/blocks/spacer'
import ToggleSwitch from 'client/shared/blocks/toggle-switch'
import Popup from 'client/shared/blocks/popup/popup'
import Button from 'client/shared/blocks/button/button'
import { connect } from 'react-redux'
import {
  CurrentUserState,
  UserPrivacySettings,
} from 'client/shared/types/current-user'
import { Dispatch, State as ConnectedState } from 'shared/types/redux'
import { updatePrivacy } from 'client/shared/reducers/current-user-reducer'
import urlFor, { QueryParams } from '../../../../shared/tools/url-helper'
import subscriptionHelper from '../../../shared/helpers/subscription-helper'
import { shouldHideSubscriptionManagement } from 'client/bookmate/selectors/subscription-selectors'
import Linka from 'client/shared/blocks/linka'
import { push } from 'react-router-redux'

export const privacyApiConstants = {
  booksHidden: 'content_visibility',
  shelvesHidden: 'bookshelves_visibility',
  newShelvesHidden: 'new_bookshelf_visible',
  booksShow: 'for_all',
  booksHide: 'for_none',
  shelvesShow: 'for_all',
  shelvesHide: 'for_none',
  newShelvesShow: true,
  newShelvesHide: false,
  categoryShown: 'for_all',
  categoryHidden: 'for_none',
}

type Props = {
  settings: UserPrivacySettings
  currentUser: CurrentUserState
  hasRecurrent: boolean
  hideSubscriptionManagement: boolean
  dispatch: Dispatch
  query?: QueryParams
}

type State = {
  popupShown: boolean
  popupPrivacyShown: boolean
  booksHidden: boolean
  shelvesHidden: boolean
  newShelvesHidden: boolean
  currentToggler: string
  popupContent: string
}

class UserSettingsPrivacy extends React.Component<Props, State> {
  state: State = {
    popupShown: false,
    popupPrivacyShown: false,
    booksHidden:
      this.props.settings.content_visibility ===
      privacyApiConstants.categoryHidden,
    shelvesHidden:
      this.props.settings.bookshelves_visibility ===
      privacyApiConstants.categoryHidden,
    newShelvesHidden: !this.props.settings.new_bookshelf_visible,
    currentToggler: '',
    popupContent: '',
  }

  onSwitch = (toggler: string) => (): void => {
    if (toggler === 'newShelvesHidden') {
      this.setState(
        {
          currentToggler: toggler,
          popupContent: this.defineTogglerAction(toggler),
        },
        () => this.confirmAction(),
      )
    } else {
      this.openPopup(toggler)
    }
  }

  openPopup = (toggler: string): void => {
    const action = this.defineTogglerAction(toggler)
    this.setState(prev => ({
      ...prev,
      popupShown: true,
      popupContent: action,
      currentToggler: toggler,
    }))
  }

  openPrivacyPopup = () => {
    this.setState(prev => ({ ...prev, popupPrivacyShown: true }))
  }

  confirmPrivacyAction = () => {
    const query = this.props.query || {}
    const login: string | undefined = this.props.currentUser.data?.login
    const email: string | undefined = this.props.currentUser.data?.email
    const usedConfirmationMethod = email ? 'password' : 'sms-code'

    this.props.dispatch(
      push(
        urlFor.deleteAccount({
          login,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          option: usedConfirmationMethod,
          query,
        }),
      ),
    )
  }

  confirmAction = async (): Promise<void> => {
    const { dispatch, settings } = this.props
    const { currentToggler, popupContent } = this.state

    const body = {
      ...settings,
      [privacyApiConstants[currentToggler]]: privacyApiConstants[popupContent],
      ...(popupContent === 'shelvesHide' && { new_bookshelf_visible: false }),
    }
    await dispatch(updatePrivacy(body))
  }

  closePopup = (): void => {
    this.setState(prev => ({
      ...prev,
      popupShown: false,
      popupPrivacyShown: false,
    }))
  }

  defineTogglerAction = (toggler: string): string => {
    let action
    switch (toggler) {
      case 'booksHidden': {
        action = this.state.booksHidden ? 'booksShow' : 'booksHide'
        break
      }
      case 'shelvesHidden': {
        action = this.state.shelvesHidden ? 'shelvesShow' : 'shelvesHide'
        break
      }
      case 'newShelvesHidden': {
        action = this.state.newShelvesHidden
          ? 'newShelvesShow'
          : 'newShelvesHide'
        break
      }
    }
    return action
  }

  componentDidUpdate(prevProps) {
    if (this.props.settings !== prevProps.settings) {
      const { currentToggler } = this.state
      const newState = {
        [currentToggler]: !this.state[currentToggler],
        ...(currentToggler === 'shelvesHidden' &&
          !this.state.shelvesHidden && {
            newShelvesHidden: true,
          }),
      }
      this.setState({
        ...this.state,
        ...newState,
      })
      this.closePopup()
    }
  }

  renderPopup(): JSX.Element {
    const { popupContent } = this.state
    const elements = {
      heading: <Trans id="profile.settings_privacy_popup_books_hide_title" />,
      text: <Trans id="profile.settings_privacy_popup_books_hide_confirm" />,
      button: <Trans id="profile.settings_privacy_popup_books_show_confirm" />,
    }
    switch (popupContent) {
      case 'booksShow': {
        elements.heading = (
          <Trans id="profile.settings_privacy_popup_books_show_title" />
        )
        elements.text = (
          <Trans id="profile.settings_privacy_popup_books_show_text" />
        )
        elements.button = (
          <Trans id="profile.settings_privacy_popup_books_show_confirm" />
        )
        break
      }
      case 'booksHide': {
        elements.heading = (
          <Trans id="profile.settings_privacy_popup_books_hide_title" />
        )
        elements.text = (
          <Trans id="profile.settings_privacy_popup_books_hide_text" />
        )
        elements.button = (
          <Trans id="profile.settings_privacy_popup_books_hide_confirm" />
        )
        break
      }
      case 'shelvesShow': {
        elements.heading = (
          <Trans id="profile.settings_privacy_popup_shelves_show_title" />
        )
        elements.text = (
          <Trans id="profile.settings_privacy_popup_shelves_show_text" />
        )
        elements.button = (
          <Trans id="profile.settings_privacy_popup_shelves_show_confirm" />
        )
        break
      }
      case 'shelvesHide': {
        elements.heading = (
          <Trans id="profile.settings_privacy_popup_shelves_hide_title" />
        )
        elements.text = (
          <Trans id="profile.settings_privacy_popup_shelves_hide_text" />
        )
        elements.button = (
          <Trans id="profile.settings_privacy_popup_shelves_hide_confirm" />
        )
        break
      }
    }

    return (
      <Popup onClose={this.closePopup} className="user-settings-privacy__popup">
        <Heading rank={3}>{elements.heading}</Heading>
        <p>{elements.text}</p>
        <Button
          className="user-settings-privacy__popup-button"
          onClick={this.confirmAction}
        >
          {elements.button}
        </Button>
        <Button
          kind="transparent"
          className="user-settings-privacy__popup-button user-settings-privacy__popup-button_type-cancel"
          onClick={this.closePopup}
        >
          <Trans id="profile.settings_devices_cancel_action" />
        </Button>
      </Popup>
    )
  }

  renderPrivacyPopup(): JSX.Element {
    const email: string | undefined = this.props.currentUser.data?.email
    const query = this.props.query || {}

    return (
      <Popup onClose={this.closePopup} className="user-settings-cookies__popup">
        <Heading rank={3}>
          <Trans id="profile.settings_cookies_popup_header" />
        </Heading>
        <p className="user-settings-cookies__align-left">
          <Trans id="profile.settings_privacy_policy_popup_text" />
        </p>
        {this.props.hasRecurrent && !this.props.hideSubscriptionManagement && (
          <p className="user-settings-cookies__red-color user-settings-cookies__align-left">
            <Trans
              id="profile.cancel_subscription_warning"
              components={[
                <Linka path={urlFor.manageSubscriptions(query)}></Linka>,
              ]}
            />
          </p>
        )}

        <Button
          disabled={!email || this.props.hasRecurrent}
          className="user-settings-cookies__popup-button"
          onClick={this.confirmPrivacyAction}
        >
          <Trans id="profile.settings_cookies_popup_delete_button" />
        </Button>

        <Button
          kind="transparent"
          className="user-settings-cookies__popup-button user-settings-cookies__popup-button_type-cancel"
          onClick={this.closePopup}
        >
          <Trans id="profile.settings_cookies_popup_cancel_button" />
        </Button>
      </Popup>
    )
  }

  render(): JSX.Element {
    const {
      popupShown,
      popupPrivacyShown,
      booksHidden,
      shelvesHidden,
      newShelvesHidden,
    } = this.state

    return (
      <>
        {popupShown && this.renderPopup()}
        {popupPrivacyShown && this.renderPrivacyPopup()}
        <Heading rank={2}>
          <Trans id="profile.settings_privacy" />
        </Heading>
        <Spacer size={32} />
        <div>
          <div className="user-settings-privacy__toggle-block">
            <ToggleSwitch onChange={this.openPrivacyPopup} checked />
            <div>
              <Heading
                rank={4}
                className="user-settings-privacy__toggle-heading"
              >
                <Trans id="profile.settings_privacy_checkbox_header" />
              </Heading>
              <p className="user-settings-privacy__toggle-text">
                <Linka path={urlFor.legal('/#cookies', this.props.query)}>
                  <Trans id="profile.settings_privacy_checkbox_text" />
                </Linka>
              </p>
            </div>
          </div>

          <div className="user-settings-privacy__toggle-block">
            <ToggleSwitch
              onChange={this.onSwitch('booksHidden')}
              checked={booksHidden}
            />
            <div>
              <Heading
                rank={4}
                className="user-settings-privacy__toggle-heading"
              >
                <Trans id="profile.settings_privacy_books" />
              </Heading>
              <p className="user-settings-privacy__toggle-text">
                {booksHidden ? (
                  <Trans id="profile.settings_privacy_books_on" />
                ) : (
                  <Trans id="profile.settings_privacy_books_off" />
                )}
              </p>
            </div>
          </div>
          <div className="user-settings-privacy__toggle-block">
            <ToggleSwitch
              onChange={this.onSwitch('shelvesHidden')}
              checked={shelvesHidden}
            />
            <div>
              <Heading
                rank={4}
                className="user-settings-privacy__toggle-heading"
              >
                <Trans id="profile.settings_privacy_shelves" />
              </Heading>
              <p className="user-settings-privacy__toggle-text">
                {shelvesHidden ? (
                  <Trans id="profile.settings_privacy_shelves_on" />
                ) : (
                  <Trans id="profile.settings_privacy_shelves_off" />
                )}
              </p>
            </div>
          </div>
          <div className="user-settings-privacy__toggle-block">
            <ToggleSwitch
              onChange={this.onSwitch('newShelvesHidden')}
              checked={newShelvesHidden}
              disabled={shelvesHidden}
            />
            <div>
              <Heading
                rank={4}
                className={cn('user-settings-privacy__toggle-heading', {
                  disabled: shelvesHidden,
                })}
              >
                <Trans id="profile.settings_privacy_new_shelves" />
              </Heading>
            </div>
          </div>
        </div>
      </>
    )
  }
}

const connectWrapper = connect((state: ConnectedState) => ({
  settings: state.currentUser.ui.privacy.data,
  currentUser: state.currentUser,
  hasRecurrent: subscriptionHelper.hasBilledRecurrent(
    state.subscription.userSubscriptions.recurrents,
  ),
  hideSubscriptionManagement: shouldHideSubscriptionManagement(state),
}))

export default connectWrapper(UserSettingsPrivacy)
